import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { 
  TrendingUp, 
  Check, 
  ArrowRight,
  Calculator,
  Star,
  Shield,
  Handshake,
  Crown,
  FileText,
  BadgeCheck,
  BarChart3,
  Clock,
  Users,
  Home
} from "lucide-react";
import { getPublicFormules, getPublicTemoignages, simulerRentabilite, createLead } from "@/lib/api";

const processSteps = [
  { step: "1", title: "Analyse", description: "Étude gratuite du bien" },
  { step: "2", title: "Signature", description: "Signature du contrat de bail civil" },
  { step: "3", title: "Mise en location", description: "Photos et annonces pro" },
  { step: "4", title: "Gestion complète", description: "On s'occupe de tout" },
];

export default function ProprietairesPage() {
  const [formules, setFormules] = useState([]);
  const [temoignages, setTemoignages] = useState([]);
  const [simulateur, setSimulateur] = useState({
    prix_achat: 150000,
    surface: 50,
    type_bien: "appartement",
    capacite: 4,
    charges_mensuelles: 200
  });
  const [simulation, setSimulation] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    telephone: "",
    ville: "Limoges",
    type_bien: "appartement",
    surface: "",
    objectif: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [formulesRes, temoignagesRes] = await Promise.all([
        getPublicFormules(),
        getPublicTemoignages("proprietaire")
      ]);
      setFormules(formulesRes.data);
      setTemoignages(temoignagesRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const handleSimulation = async () => {
    try {
      const res = await simulerRentabilite(simulateur);
      setSimulation(res.data);
    } catch (error) {
      console.error("Error simulating:", error);
      toast.error("Erreur lors de la simulation");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createLead({
        type_lead: "proprietaire",
        ...formData,
        surface: parseInt(formData.surface) || 0
      });
      toast.success("Demande envoyée ! Nous vous recontactons sous 24h.");
      setFormData({
        nom: "",
        email: "",
        telephone: "",
        ville: "Limoges",
        type_bien: "appartement",
        surface: "",
        objectif: ""
      });
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      {/* Hero with background */}
      <section className="relative py-24 overflow-hidden" data-testid="proprietaires-hero">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/7736029/pexels-photo-7736029.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C1E14]/80" />
        </div>
        <div className="relative container-hh">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block bg-[#C9922A]/20 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm border border-[#C9922A]/30">Propriétaires</span>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-6">
                Deux solutions, un même objectif : valoriser votre bien
              </h1>
              <p className="text-white/80 text-lg">
                Que vous cherchiez un loyer garanti ou une alternative en mandat de prestation dynamique, 
                nous donnons vie à vos projets immobiliers à Limoges et Évaux-les-Bains.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Processus en 4 étapes */}
      <section className="section-hh bg-[#EDE8E1]" data-testid="processus-section">
        <div className="container-hh">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#004333]/10 text-[#004333] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#004333]/20">Comment ça marche</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">
              Un processus simple en 4 étapes
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center relative"
              >
                <div className="w-16 h-16 bg-[#C9922A] text-white font-mono text-2xl font-bold rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  {item.step}
                </div>
                <h3 className="font-display text-lg text-[#004333] mb-2">
                  {item.title}
                </h3>
                <p className="text-[#8B7D6B] text-sm">
                  {item.description}
                </p>
                {index < 3 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-[#D4C9B8]" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Simulateur de rentabilité */}
      <section className="section-hh bg-[#2C1E14]" data-testid="simulateur-section">
        <div className="container-hh">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="text-white">
              <span className="badge-secondary mb-4 inline-block">Simulateur</span>
              <h2 className="font-display text-3xl md:text-4xl mb-6">
                Calculez votre rentabilité
              </h2>
              <p className="text-white/70 mb-8">
                Estimez les revenus potentiels de votre bien en location courte durée 
                et découvrez votre rentabilité nette.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Prix d'achat du bien (€)
                  </label>
                  <input
                    type="number"
                    value={simulateur.prix_achat}
                    onChange={(e) => setSimulateur({ ...simulateur, prix_achat: parseFloat(e.target.value) })}
                    className="input-hh bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="simulateur-prix"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Type de bien
                    </label>
                    <select
                      value={simulateur.type_bien}
                      onChange={(e) => setSimulateur({ ...simulateur, type_bien: e.target.value })}
                      className="input-hh bg-white/10 border-white/20 text-white"
                      data-testid="simulateur-type"
                    >
                      <option value="studio">Studio</option>
                      <option value="appartement">Appartement</option>
                      <option value="maison">Maison</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Surface (m²)
                    </label>
                    <input
                      type="number"
                      value={simulateur.surface}
                      onChange={(e) => setSimulateur({ ...simulateur, surface: parseInt(e.target.value) })}
                      className="input-hh bg-white/10 border-white/20 text-white"
                      data-testid="simulateur-surface"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Capacité (pers.)
                    </label>
                    <input
                      type="number"
                      value={simulateur.capacite}
                      onChange={(e) => setSimulateur({ ...simulateur, capacite: parseInt(e.target.value) })}
                      className="input-hh bg-white/10 border-white/20 text-white"
                      data-testid="simulateur-capacite"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Charges mensuelles (€)
                    </label>
                    <input
                      type="number"
                      value={simulateur.charges_mensuelles}
                      onChange={(e) => setSimulateur({ ...simulateur, charges_mensuelles: parseFloat(e.target.value) })}
                      className="input-hh bg-white/10 border-white/20 text-white"
                      data-testid="simulateur-charges"
                    />
                  </div>
                </div>
                
                <button
                  onClick={handleSimulation}
                  className="btn-accent w-full flex items-center justify-center gap-2"
                  data-testid="simulateur-submit"
                >
                  <Calculator className="w-5 h-5" />
                  Calculer ma rentabilité
                </button>
              </div>
            </div>
            
            <div>
              {simulation ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white p-8"
                >
                  <h3 className="font-display text-2xl text-[#004333] mb-6">
                    Résultats de votre simulation
                  </h3>
                  
                  <div className="space-y-4 mb-8">
                    <div className="flex justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600">Prix par nuit estimé</span>
                      <span className="font-mono font-bold text-[#004333]">{simulation.prix_nuit_estime} €</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600">Taux d'occupation</span>
                      <span className="font-mono font-bold text-[#004333]">{simulation.taux_occupation}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600">Revenu brut mensuel</span>
                      <span className="font-mono font-bold text-[#004333]">{simulation.revenu_brut_mensuel} €</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600">Revenu net mensuel</span>
                      <span className="font-mono text-xl font-bold text-[#C9922A]">{simulation.revenu_net_mensuel} €</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600">Revenu net annuel</span>
                      <span className="font-mono text-xl font-bold text-[#004333]">{simulation.revenu_net_annuel} €</span>
                    </div>
                  </div>
                  
                  <div className="bg-[#FAFAF8] p-6 mb-6">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Rentabilité brute</div>
                        <div className="font-mono text-2xl font-bold text-[#004333]">{simulation.rentabilite_brute}%</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Rentabilité nette</div>
                        <div className="font-mono text-2xl font-bold text-[#C9922A]">{simulation.rentabilite_nette}%</div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-6">
                    *Estimation indicative basée sur les données du marché limougeaud.
                  </p>
                  
                  <a href="#contact-form" className="btn-primary w-full text-center block">
                    Obtenir une étude personnalisée
                  </a>
                </motion.div>
              ) : (
                <div className="bg-white/5 p-8 text-center backdrop-blur-sm">
                  <TrendingUp className="w-16 h-16 text-[#C9922A] mx-auto mb-4" />
                  <h3 className="font-display text-xl text-white mb-2">
                    Votre simulation
                  </h3>
                  <p className="text-white/60">
                    Remplissez le formulaire pour découvrir le potentiel de votre bien.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== LE BAIL CIVIL — Explication ===================== */}
      <section className="py-16 lg:py-20 bg-[#F5F0EB]" data-testid="bail-civil-section">
        <div className="container-hh">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-10 items-center">
              {/* Text */}
              <div className="lg:col-span-3">
                <span className="inline-block bg-[#C9922A]/15 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#C9922A]/25">Cadre juridique</span>
                <h2 className="font-display text-2xl md:text-3xl text-[#004333] mb-4">
                  Le bail civil professionnel
                </h2>
                <p className="text-[#6B5E50] leading-relaxed mb-5">
                  La sous-location professionnelle est encadrée par le <strong>Code civil</strong>. 
                  Vous louez votre bien à Happy House via un bail Code civil professionnel. 
                  Nous devenons votre locataire unique et prenons en charge la gestion complète de 
                  l'hébergement de nos voyageurs. C'est un contrat de droit commun, différent du bail d'habitation classique.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: Shield, title: "Durée flexible", desc: "Négociée entre les parties, généralement 1 à 3 ans" },
                    { icon: BadgeCheck, title: "Loyer garanti", desc: "Un loyer fixe versé chaque mois, sans exception" },
                    { icon: FileText, title: "100% légal", desc: "Encadré par le Code civil, aucune autorisation nécessaire" },
                    { icon: Home, title: "Bien préservé", desc: "Votre bien est entretenu et valorisé en permanence" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white border border-[#E0D6CA] shadow-sm">
                      <div className="w-9 h-9 rounded-lg bg-[#C9922A]/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4.5 h-4.5 text-[#C9922A]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#004333]">{item.title}</p>
                        <p className="text-xs text-[#8B7D6B] leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Visual */}
              <div className="lg:col-span-2">
                <div className="bg-[#004333] rounded-2xl p-6 text-white">
                  <h3 className="font-display text-lg mb-4">En résumé</h3>
                  <ul className="space-y-3 text-sm">
                    {[
                      "Vous restez propriétaire de votre bien",
                      "Happyhouse conciergerie est votre locataire unique",
                      "Loyer fixe garanti chaque mois",
                      "Zéro gestion de votre côté",
                      "Assurance RC professionnelle incluse",
                      "Restitution du bien selon contrat",
                    ].map((point, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-[#C9922A] flex-shrink-0 mt-0.5" />
                        <span className="text-white/80">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== NOS OFFRES — Sérénité vs Partenariat ===================== */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-[#1A120B]" data-testid="formules-section">
        {/* Noise texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"}} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#C9922A]/[0.03] blur-[150px] pointer-events-none" />

        <div className="container-hh relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block bg-[#C9922A]/10 text-[#C9922A] px-5 py-1.5 rounded-full text-sm font-semibold mb-5 border border-[#C9922A]/20 tracking-wide"
            >
              Nos offres
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-4"
            >
              Nos offres de gestion
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-white/50 max-w-xl mx-auto"
            >
              Deux formules adaptées à vos objectifs. Une seule promesse : valoriser votre bien.
            </motion.p>
          </div>

          {/* Deux offres côte à côte */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">

            {/* ─── OFFRE SÉRÉNITÉ (prioritaire) ─── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative group"
              data-testid="offre-serenite"
            >
              {/* Golden animated border */}
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#C9922A] via-[#E8C874] to-[#C9922A] opacity-60 group-hover:opacity-100 transition-opacity duration-500 blur-[1px]" />
              
              <div className="relative bg-[#1A120B] rounded-2xl p-8 lg:p-10 border border-[#C9922A]/30 h-full flex flex-col">
                {/* Badge Choix favori */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#C9922A] to-[#E8C874] text-[#1A120B] text-xs font-bold px-5 py-1.5 rounded-full shadow-[0_4px_16px_rgba(201,146,42,0.4)] uppercase tracking-wider" data-testid="badge-choix-favori">
                    <Crown className="w-3.5 h-3.5" />
                    Choix favori
                  </span>
                </div>

                {/* Icon + Title */}
                <div className="flex items-center gap-4 mt-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#C9922A]/15 border border-[#C9922A]/25 flex items-center justify-center">
                    <Shield className="w-7 h-7 text-[#C9922A]" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-white">Offre Sérénité</h3>
                    <p className="text-[#C9922A] text-sm font-medium">Sous-location professionnelle</p>
                  </div>
                </div>

                {/* Key argument */}
                <div className="bg-[#C9922A]/[0.08] border border-[#C9922A]/15 rounded-xl p-5 mb-6">
                  <p className="text-white font-semibold text-lg leading-snug mb-2">
                    Revenus sécurisés et tranquillité absolue.
                  </p>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Vous louez votre bien à Happy House via un <strong className="text-white/80">bail Code civil professionnel</strong>. Nous devenons votre locataire unique et prenons en charge la gestion complète de l'hébergement de nos voyageurs. Pour vous, c'est l'assurance d'un loyer versé chaque mois sans faute, d'un entretien rigoureux et d'une totale tranquillité d'esprit. Votre patrimoine est valorisé, sans les contraintes de la gestion locative classique (vacance locative, trêve hivernale…).
                  </p>
                </div>

                {/* Contract type */}
                <div className="flex items-center gap-3 mb-6 px-1">
                  <FileText className="w-4 h-4 text-[#C9922A] flex-shrink-0" />
                  <span className="text-white/70 text-sm">Contrat : <strong className="text-white">Bail civil professionnel</strong></span>
                </div>

                {/* Comment ça fonctionne */}
                <div className="mb-6">
                  <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span className="w-4 h-px bg-[#C9922A]" />
                    Concrètement, comment ça fonctionne ?
                  </h4>
                  <div className="space-y-3">
                    {[
                      { num: "01", label: "Analyse", desc: "Étude gratuite de votre bien" },
                      { num: "02", label: "Signature", desc: "Signature du contrat de bail civil" },
                      { num: "03", label: "Mise en location", desc: "Photos et annonces professionnelles" },
                      { num: "04", label: "Gestion complète", desc: "On s'occupe de tout" },
                    ].map((step) => (
                      <div key={step.num} className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full bg-[#C9922A]/20 border border-[#C9922A]/30 flex items-center justify-center text-[#C9922A] text-xs font-bold flex-shrink-0">
                          {step.num}
                        </span>
                        <span className="text-white/80 text-sm font-medium">{step.label}</span>
                        <span className="text-white/40 text-xs">— {step.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ce que vous gagnez */}
                <div className="mb-8 flex-1">
                  <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span className="w-4 h-px bg-[#C9922A]" />
                    Ce que vous gagnez
                  </h4>
                  <ul className="space-y-3">
                    {[
                      { title: "Paiement garanti", desc: "Votre loyer est versé à date fixe, même si le logement est inoccupé." },
                      { title: "Entretien rigoureux", desc: "Nous remettons le bien à neuf après chaque voyageur. C'est notre intérêt autant que le vôtre." },
                      { title: "Zéro frais", desc: "Pas de frais de gestion, pas de commissions. Le montant du loyer est net pour vous." },
                      { title: "Rapport mensuel détaillé", desc: "Transparence totale sur la gestion de votre patrimoine." },
                      { title: "Un interlocuteur unique dédié", desc: "Une équipe locale disponible 7j/7." },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <BadgeCheck className="w-4 h-4 text-[#C9922A] flex-shrink-0 mt-0.5" />
                        <span>
                          <strong className="text-white">{item.title}</strong>
                          <span className="text-white/50"> — {item.desc}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pour qui */}
                <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 mb-6 text-sm text-white/60 leading-relaxed">
                  <span className="text-white/80 font-medium">Pour qui ?</span> Propriétaires recherchant une solution idéale pour un investissement serein qui procure stabilité, sécurité et simplicité. Vous souhaitez des revenus garantis, sans aléas ni gestion ?
                </div>

                {/* Phrase finale */}
                <p className="text-white/50 text-xs text-center italic mb-5">
                  Ne laissez plus votre bien sous-exploité —
                </p>

                {/* CTA */}
                <a
                  href="#contact-form"
                  className="block w-full text-center py-3.5 px-6 bg-gradient-to-r from-[#C9922A] to-[#E8C874] text-[#1A120B] font-bold rounded-full shadow-[0_4px_20px_rgba(201,146,42,0.35)] hover:shadow-[0_8px_32px_rgba(201,146,42,0.5)] hover:-translate-y-0.5 transition-all duration-300 text-sm uppercase tracking-wider"
                  data-testid="cta-serenite"
                >
                  Confiez-le nous et gardez l'esprit libre !
                </a>
              </div>
            </motion.div>

            {/* ─── OFFRE PARTENARIAT (conciergerie) ─── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25 }}
              className="relative group"
              data-testid="offre-partenariat"
            >
              <div className="relative bg-white/[0.04] backdrop-blur-sm rounded-2xl p-8 lg:p-10 border border-white/[0.08] h-full flex flex-col hover:border-white/15 transition-all duration-500">
                {/* Icon + Title */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
                    <Handshake className="w-7 h-7 text-white/70" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-white">Offre Partenariat</h3>
                    <p className="text-white/40 text-sm font-medium">Conciergerie en mandat</p>
                  </div>
                </div>

                {/* Key argument */}
                <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 mb-6">
                  <p className="text-white font-semibold text-lg leading-snug">
                    Optimisez vos revenus tout en gardant le contrôle.
                  </p>
                  <p className="text-white/40 text-sm mt-2">
                    Vous souhaitez garder le contrôle de votre bien tout en optimisant vos revenus locatifs ? 
                    Notre service de conciergerie privée vous permet de tirer pleinement parti du potentiel de 
                    votre logement, grâce à une gestion professionnelle adaptée aux variations du marché 
                    (saisonnalité, demande locale, événements...).
                  </p>
                </div>

                {/* Contract type */}
                <div className="flex items-center gap-3 mb-6 px-1">
                  <FileText className="w-4 h-4 text-white/40 flex-shrink-0" />
                  <span className="text-white/50 text-sm">Contrat : <strong className="text-white/80">Mandat de prestation de services</strong></span>
                </div>

                {/* Benefits list */}
                <ul className="space-y-3 mb-8 flex-1">
                  {[
                    "Revenus potentiellement plus élevés grâce au yield management",
                    "Zéro contrainte au quotidien",
                    "Maîtrise totale de votre bien et de vos encaissements",
                    "Gestion complète ou partielle selon vos besoins",
                    "Ménage professionnel et maintenance",
                    "Relation voyageurs 7j/7",
                    "Création et diffusion des annonces",
                    "Résiliation flexible",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/50 text-sm">
                      <Check className="w-4 h-4 text-white/30 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href="#contact-form"
                  className="block w-full text-center py-3.5 px-6 border border-white/20 text-white/80 font-semibold rounded-full hover:bg-white/[0.06] hover:border-white/30 hover:-translate-y-0.5 transition-all duration-300 text-sm uppercase tracking-wider"
                  data-testid="cta-partenariat"
                >
                  Choisir l'Offre Partenariat
                </a>
              </div>
            </motion.div>
          </div>

          {/* Comparaison rapide */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-12 max-w-3xl mx-auto"
          >
            <div className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-3 text-center">
                <div className="py-4 px-3 border-b border-white/[0.06]" />
                <div className="py-4 px-3 border-b border-[#C9922A]/20 bg-[#C9922A]/[0.05]">
                  <span className="text-[#C9922A] text-xs font-bold uppercase tracking-wider">Sérénité</span>
                </div>
                <div className="py-4 px-3 border-b border-white/[0.06]">
                  <span className="text-white/40 text-xs font-bold uppercase tracking-wider">Partenariat</span>
                </div>

                {[
                  { label: "Revenus", serenite: "Loyer fixe garanti", partenariat: "Variables (saisonnalité)" },
                  { label: "Risque", serenite: "Zéro", partenariat: "Modéré (vacance)" },
                  { label: "Gestion", serenite: "Aucune", partenariat: "Aucune" },
                  { label: "Contrat", serenite: "Bail civil", partenariat: "Mandat de prestation" },
                ].map((row, i) => (
                  <div key={i} className="contents">
                    <div className="py-3.5 px-4 border-b border-white/[0.04] text-left">
                      <span className="text-white/50 text-sm">{row.label}</span>
                    </div>
                    <div className="py-3.5 px-4 border-b border-[#C9922A]/10 bg-[#C9922A]/[0.03]">
                      <span className="text-white/80 text-sm font-medium">{row.serenite}</span>
                    </div>
                    <div className="py-3.5 px-4 border-b border-white/[0.04]">
                      <span className="text-white/50 text-sm">{row.partenariat}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo Banner before Témoignages */}
      <div className="photo-banner">
        <img src="https://images.unsplash.com/photo-1773847400278-3b409bd5aa76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxjb3p5JTIwd2FybSUyMGxpdmluZyUyMHJvb20lMjBpbnRlcmlvciUyMGZpcmVwbGFjZSUyMHdvb2R8ZW58MHx8fHwxNzc0MDI1ODM1fDA&ixlib=rb-4.1.0&q=85" alt="Salon chaleureux" loading="lazy" />
      </div>

      {/* Témoignages */}
      {temoignages.length > 0 && (
        <section className="section-hh bg-[#EDE8E1]" data-testid="temoignages-proprietaires">
          <div className="container-hh">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#004333]/10 text-[#004333] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#004333]/20">Témoignages</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#004333]">
                Ce que disent nos propriétaires
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {temoignages.slice(0, 2).map((temoignage) => (
                <div key={temoignage.id} className="bg-[#FFFDF9] p-8 rounded-xl shadow-[0_2px_12px_rgba(44,30,20,0.06)] border border-[#E0D6CA]">
                  <div className="flex gap-1 mb-4">
                    {[...Array(temoignage.note)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#C9922A] text-[#C9922A]" />
                    ))}
                  </div>
                  <p className="text-[#6B5E50] mb-6 italic text-lg">
                    "{temoignage.texte}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#004333] rounded-full flex items-center justify-center text-white font-semibold">
                      {temoignage.nom.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-[#004333]">{temoignage.nom}</div>
                      <div className="text-sm text-[#8B7D6B]">Propriétaire partenaire</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Formulaire de contact */}
      <section id="contact-form" className="section-hh bg-[#F5F0EB]" data-testid="contact-proprietaires">
        <div className="container-hh">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#C9922A]/15 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#C9922A]/25">Contact</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#004333]">
                Demandez votre étude gratuite
              </h2>
              <p className="text-[#6B5E50] mt-4">
                Remplissez ce formulaire et nous vous recontactons sous 24h.
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="bg-[#FFFDF9] p-8 rounded-xl shadow-[0_4px_24px_rgba(44,30,20,0.08)] border border-[#E0D6CA]">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="input-hh"
                    data-testid="form-nom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-hh"
                    data-testid="form-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    className="input-hh"
                    data-testid="form-telephone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ville du bien
                  </label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                    className="input-hh"
                    data-testid="form-ville"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de bien
                  </label>
                  <select
                    value={formData.type_bien}
                    onChange={(e) => setFormData({ ...formData, type_bien: e.target.value })}
                    className="input-hh"
                    data-testid="form-type"
                  >
                    <option value="studio">Studio</option>
                    <option value="appartement">Appartement</option>
                    <option value="maison">Maison</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Surface (m²)
                  </label>
                  <input
                    type="number"
                    value={formData.surface}
                    onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                    className="input-hh"
                    data-testid="form-surface"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Votre objectif
                </label>
                <textarea
                  rows={4}
                  value={formData.objectif}
                  onChange={(e) => setFormData({ ...formData, objectif: e.target.value })}
                  className="input-hh"
                  placeholder="Décrivez vos attentes..."
                  data-testid="form-objectif"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="btn-accent w-full mt-6"
                data-testid="form-submit"
              >
                {loading ? "Envoi en cours..." : "Envoyer ma demande"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
